/* eslint-disable camelcase */
import { formatDate } from './common';
import {
  SOURCE,
  INTENT,
  OCCUPANCY,
  ACCOMMODATION,
  PROPERTY_TYPES,
  DEFAULT_COORDINATES
} from '../constants/realestate';

export const calculateCarpetArea = builtUpArea => {
  if (builtUpArea.trim() === '') {
    return '';
  }

  const carpetArea = Number((builtUpArea * 80) / 100);

  return Number(carpetArea).toFixed(0);
};

export const isValidBuiltUpArea = (builtUpArea, unitType) => {
  /* const validation = {
    ONE_RK: builtUpArea >= 200 && builtUpArea <= 1400,
    ONE_BHK: builtUpArea >= 200 && builtUpArea <= 1400,
    TWO_BHK: builtUpArea >= 400 && builtUpArea <= 2400,
    THREE_BHK: builtUpArea >= 500 && builtUpArea <= 4000,
    FOUR_BHK: builtUpArea >= 600
  }; */

  return true;
};

export const getPriceText = (
  { available, priceText, price, cfCredits, adCredits },
  paymentType,
  isAddon = false
) => {
  if (priceText) return priceText;
  if (isAddon && !available) {
    return 'Not available';
  }
  if (paymentType !== 'AMOUNT') {
    const credits = paymentType === 'CF_CREDIT' ? cfCredits : adCredits;
    if (credits != null) {
      return credits === 0
        ? isAddon
          ? 'No Additional Credits'
          : 'FREE'
        : `${credits} credit${credits > 1 ? 's' : ''}`;
    }
  }
  return price > 0 ? `\u20B9${price}/-` : isAddon ? 'No Additional Price' : 'FREE';
};

const identifyBrokerageType = brokerageTerms => {
  if (brokerageTerms === 'No') {
    return 'NO';
  } else if (brokerageTerms.indexOf('Rs.') !== -1) {
    return 'AMOUNT';
  } else {
    switch (brokerageTerms.split(' ')[1]) {
      case 'Percentage':
      case 'PercentageDeal':
        return 'PERCENTAGE';
      case 'Days_Rent':
      case 'Month_Rent':
        return 'BASED_ON_RENT';
      default:
        return 'NO';
    }
  }
};

const parseBrokerageTerms = value => {
  if (value === 'No') {
    return '';
  } else if (value.indexOf('Rs.') === 0) {
    return value.replace('Rs.', '');
  } else {
    return value.split(' ')[0];
  }
};

export const getIntent = category => {
  switch (SOURCE) {
    case 'CF':
      return INTENT[SOURCE];
    case 'QH': {
      const _category = category || 'RESIDENTIAL';
      return INTENT[SOURCE][_category];
    }
  }
};

export const getPropertyTypes = (category, intent) => {
  const _intent = intent || 'SELL';
  switch (SOURCE) {
    case 'CF': {
      return PROPERTY_TYPES[SOURCE][_intent];
    }
    case 'QH': {
      const _category = category || 'RESIDENTIAL';
      const propertyTypes = PROPERTY_TYPES[SOURCE][_category][_intent];
      const defaultPropertyTypes = PROPERTY_TYPES[SOURCE][_category].SELL;
      return propertyTypes || defaultPropertyTypes;
    }
  }
};

export const getPropertyTypeLabel = (propertyType, category, intent) => {
  const propertyTypes = getPropertyTypes(category, intent);
  return propertyTypes.find(pt => pt.value === propertyType).label || '';
};

export const getAccommodation = (category, intent) => {
  if (category !== 'RESIDENTIAL') return false;
  if (!['RENT', 'PG', 'FLATMATE'].includes(intent)) return false;

  return ACCOMMODATION[SOURCE] ? ACCOMMODATION[SOURCE][category][intent] : false;
};

export const getOccupancy = intent => {
  if (!['PG', 'FLATMATE'].includes(intent)) return false;
  return OCCUPANCY[SOURCE];
};

export const isRental = intent => {
  return ['RENT', 'PG', 'FLATMATE'].includes(intent);
};

export const isPlot = propertyType => {
  return propertyType === 'PLOT' || propertyType === 'LAND';
};

export const hasUnitType = propertyType => {
  return isPlot(propertyType) ? false : !['OFFICE', 'SHOP', 'COMPLEX'].includes(propertyType);
};

export const hasFloor = propertyType => {
  return ['APARTMENT', 'BUILDER_FLOOR', 'STUDIO_APARTMENT', 'OFFICE', 'SHOP'].includes(
    propertyType
  );
};

export const isEmpty = value => {
  return value === undefined || value === null || value === '';
};

export const parseSubcat = subcat => {
  // Category
  const residential = /apartments|hostel|villas|flatmates|residential/i;
  const commercial = /commercial/i;
  const land = /land/i;

  const category = residential.test(subcat)
    ? 'RESIDENTIAL'
    : commercial.test(subcat)
      ? 'COMMERCIAL'
      : land.test(subcat)
        ? 'AGRICULTURE'
        : '';

  // Property Type
  const apartment = /apartments|hostel|flatmates/i;
  const villa = /villas/i;
  const builderFloor = /builder-floors/i;
  const serviceApartment = /service/i;

  const propertyType = serviceApartment.test(subcat)
    ? 'SERVICE_APARTMENT'
    : apartment.test(subcat)
      ? 'APARTMENT'
      : villa.test(subcat)
        ? 'HOUSE_VILLA'
        : builderFloor.test(subcat)
          ? 'BUILDER_FLOOR'
          : land.test(subcat)
            ? 'LAND'
            : '';

  // Intent
  const sale = /sale/i;
  const rent = /rent|service-apartments/i;
  const pg = /hostel/i;
  const flatmate = /flatmates/i;

  const intent = sale.test(subcat)
    ? 'SELL'
    : rent.test(subcat)
      ? 'RENT'
      : pg.test(subcat)
        ? 'PG'
        : flatmate.test(subcat)
          ? 'FLATMATE'
          : '';

  return { category, intent, propertyType };
};

export const transformFetchAdResponse = data => {
  const response = { ...data };
  const {
    city,
    locality,
    project,
    area_carpet_sqft,
    area_sq_feet,
    two_wheeler_parking,
    four_wheeler_parking,
    unit_view,
    intent,
    deposit,
    includes_registration_charges,
    property_type,
    property_onfloor,
    total_floors,
    user_type,
    is_brokerage_negotiable,
    bathroom,
    no_of_balcony,
    amenities,
    brokerage_terms,
    plot_site,
    user: { name, email, mobile }
  } = data;

  response.city = city.id;
  response.city_name = city.name;
  response.locality = locality.id;
  response.locality_name = locality.name;
  response.amenities = amenities ? amenities.split(',') : [];
  response.name = name;
  response.email = email;
  response.mobile = mobile;

  if (project) {
    response.project = project.id;
    response.project_name = project.name;
  }

  if (unit_view) {
    response.facing = unit_view;
  }

  if (intent === 'RENT') {
    response.deposit_price = deposit;
  } else {
    response.include_registration = includes_registration_charges === 1;
  }

  if (isPlot(property_type)) {
    response.plot_area = area_sq_feet;
    const [plot_length, plot_breadth] = plot_site.split('x');
    response.plot_length = plot_length;
    response.plot_breadth = plot_breadth;
  } else {
    response.carpet_area = area_carpet_sqft;
    response.builtup_area = area_sq_feet;
    response.no_of_balcony = no_of_balcony === null ? '' : no_of_balcony.toString();
    response.bathroom = bathroom === null ? '' : bathroom.toString();

    if (two_wheeler_parking === 'COVERED' && four_wheeler_parking === 'COVERED') {
      response.parking = 'Both';
    } else if (two_wheeler_parking === 'COVERED') {
      response.parking = '2W';
    } else if (four_wheeler_parking === 'COVERED') {
      response.parking = '4W';
    } else {
      response.parking = 'None';
    }
  }

  if (hasFloor(property_type)) {
    response.property_on_floor = property_onfloor;
    response.total_floors = total_floors;
  }

  if (user_type === 'BROKER') {
    response.brokerage_type = identifyBrokerageType(brokerage_terms);
    response.brokerage_terms = parseBrokerageTerms(brokerage_terms);
    response.brokerage_negotiable = is_brokerage_negotiable === 1;
  }

  return response;
};

export const preparePostAdPayload = input => {
  const {
    name,
    email,
    project,
    project_name,
    city,
    city_name,
    category,
    accommodation,
    occupancy,
    locality,
    locality_name,
    m_topic_global_id,
    subcatid,
    description,
    user_type,
    intent,
    available_from,
    property_type,
    project_latitude,
    project_longitude,
    project_amenities,
    is_negotiable,
    amenities,
    no_of_rooms,
    carpet_area,
    builtup_area,
    plot_area,
    plot_length,
    plot_breadth,
    price,
    bathroom,
    furnished,
    no_of_balcony,
    parking,
    facing,
    property_on_floor,
    total_floors,
    deposit_price,
    include_registration,
    sale_type,
    brokerage_type,
    brokerage_terms,
    brokerage_negotiable,
    street_address,
    image_details,
    optin,
    paint,
    home_cleaning,
    pest_control
  } = input;

  const payload = {
    name,
    email,
    project: {
      id: project,
      name: project_name
    },
    city: {
      id: city,
      name: city_name
    },
    locality: {
      id: locality,
      name: locality_name
    },
    m_topic_global_id: parseInt(m_topic_global_id),
    subcatid,
    location: locality_name,
    ad_style: '',
    user_type,
    intent,
    available_from: formatDate('yyyy-mm-dd', new Date(available_from)),
    property_type,
    map_latitude: project_latitude ? parseFloat(project_latitude) : DEFAULT_COORDINATES.LAT,
    map_longitude: project_longitude ? parseFloat(project_longitude) : DEFAULT_COORDINATES.LON,
    is_negotiable: is_negotiable ? 1 : 0,
    amenities: amenities ? amenities.join(',') : project_amenities || '',
    price: parseInt(price),
    post_ad_source: 'ORGANIC',
    land_type: category || 'RESIDENTIAL',
    street_address,
    image_details,
    optin: !!optin,
    paint: !!paint,
    home_cleaning: !!home_cleaning,
    pest_control: !!pest_control
  };

  payload.description = description.replace(/;/g, ',');

  if (!isEmpty(facing)) payload.unit_view = facing;
  if (!isEmpty(category)) payload.category = category;
  if (!isEmpty(accommodation)) payload.accommodation = accommodation;
  if (!isEmpty(occupancy)) payload.occupancy = occupancy;

  if (hasFloor(property_type)) {
    payload.property_onfloor = parseInt(property_on_floor);
    payload.total_floors = parseInt(total_floors);
  }

  if (isPlot(property_type)) {
    payload.plot_area = plot_area;
    payload.area_sq_feet = plot_area;
    payload.plot_site = `${plot_length}x${plot_breadth}`;
    payload.area_unit_plot = 'SQ_FT';
  } else {
    payload.area_carpet_sqft = carpet_area;
    payload.area_sq_feet = builtup_area;
    payload.area_unit_carpet = 'SQ_FT';
    payload.area_unit_builtup = 'SQ_FT';
    payload.furnished = furnished;

    payload.two_wheeler_parking = parking === '2W' || parking === 'Both' ? 'COVERED' : 'NO';
    payload.four_wheeler_parking = parking === '4W' || parking === 'Both' ? 'COVERED' : 'NO';

    if (!isEmpty(no_of_rooms)) payload.no_of_rooms = no_of_rooms;
    if (!isEmpty(bathroom)) payload.bathroom = parseInt(bathroom);
    if (!isEmpty(no_of_balcony)) payload.no_of_balcony = parseInt(no_of_balcony);
  }

  if (intent === 'RENT') {
    payload.deposit = parseInt(deposit_price);
  } else if (intent === 'SELL') {
    payload.includes_registration_charges = include_registration ? 1 : 0;
    payload.sale_type = sale_type;

    if (isEmpty(sale_type) || user_type === 'INDIVIDUAL' || isPlot(property_type)) {
      payload.sale_type = 'RESALE';
    }
  }

  if (user_type === 'BROKER') {
    let brokerageTerms;

    switch (brokerage_type) {
      case 'AMOUNT':
        brokerageTerms = `Rs.${brokerage_terms}`;
        break;
      case 'PERCENTAGE':
        brokerageTerms = `${brokerage_terms} Percentage`;
        break;
      case 'BASED_ON_RENT':
        brokerageTerms = `${brokerage_terms} Days_Rent`;
        break;
      default:
        brokerageTerms = 'No';
        break;
    }

    payload.brokerage_list = brokerage_type || 'NO';
    payload.brokerage_terms = brokerageTerms;
    payload.is_brokerage_negotiable = brokerage_negotiable ? 1 : 0;
  }

  return payload;
};

export const prepareEditAdPayload = (input, image_details) => {
  const {
    id,
    city,
    city_name,
    locality,
    locality_name,
    subcatid,
    description,
    available_from,
    is_negotiable,
    include_registration,
    brokerage_negotiable,
    amenities,
    carpet_area,
    builtup_area,
    plot_area,
    plot_length,
    plot_breadth,
    price,
    bathroom,
    furnished,
    parking,
    property_on_floor,
    total_floors,
    no_of_balcony,
    brokerage_type,
    brokerage_terms,
    facing,
    deposit_price,
    sale_type,
    intent,
    user_type,
    property_type,
    category,
    paint,
    home_cleaning,
    pest_control
  } = input;

  const payload = {
    id,
    city: {
      id: city,
      name: city_name
    },
    locality: {
      id: locality,
      name: locality_name
    },
    subcatid,
    available_from,
    is_negotiable: is_negotiable ? 1 : 0,
    amenities: amenities ? amenities.join(',') : '',
    price: parseInt(price),
    land_type: category || 'RESIDENTIAL',
    image_details,
    paint: !!paint,
    home_cleaning: !!home_cleaning,
    pest_control: !!pest_control
  };

  payload.description = description.replace(/;/g, ',');

  if (!isEmpty(facing)) payload.unit_view = facing;

  if (isPlot(property_type)) {
    payload.plot_area = plot_area;
    payload.area_sq_feet = plot_area;
    payload.plot_site = `${plot_length}x${plot_breadth}`;
    payload.area_unit_plot = 'SQ_FT';
  } else {
    payload.area_carpet_sqft = carpet_area;
    payload.area_sq_feet = builtup_area;
    payload.area_unit_carpet = 'SQ_FT';
    payload.area_unit_builtup = 'SQ_FT';
    payload.furnished = furnished;

    payload.two_wheeler_parking = parking === '2W' || parking === 'Both' ? 'COVERED' : 'NO';
    payload.four_wheeler_parking = parking === '4W' || parking === 'Both' ? 'COVERED' : 'NO';

    if (!isEmpty(bathroom)) payload.bathroom = parseInt(bathroom);
    if (!isEmpty(no_of_balcony)) payload.no_of_balcony = parseInt(no_of_balcony);
  }

  if (intent === 'RENT') {
    payload.deposit = parseInt(deposit_price);
  } else if (intent === 'SELL') {
    payload.includes_registration_charges = include_registration ? 1 : 0;
    payload.sale_type = sale_type;

    if (isEmpty(sale_type) || user_type === 'INDIVIDUAL' || isPlot(property_type)) {
      payload.sale_type = 'RESALE';
    }
  }

  if (hasFloor(property_type)) {
    payload.property_onfloor = parseInt(property_on_floor);
    payload.total_floors = parseInt(total_floors);
  }

  if (user_type === 'BROKER') {
    let brokerageTerms;

    switch (brokerage_type) {
      case 'AMOUNT':
        brokerageTerms = `Rs.${brokerage_terms}`;
        break;
      case 'PERCENTAGE':
        brokerageTerms = `${brokerage_terms} Percentage`;
        break;
      case 'BASED_ON_RENT':
        brokerageTerms = `${brokerage_terms} Days_Rent`;
        break;
      default:
        brokerageTerms = 'No';
        break;
    }

    payload.brokerage_list = brokerage_type;
    payload.brokerage_terms = brokerageTerms;
    payload.is_brokerage_negotiable = brokerage_negotiable ? 1 : 0;
  }

  return payload;
};
